import {
  cloneJson,
  contractError,
  validateJsonObject,
  validateSha256,
  type JsonObject,
} from '@djgg/gameplay-kit';

export const VERIFIED_CORE_V2_RECIPE = Object.freeze({ id: 'verified-core', revision: 2 as const });
export const QUALITY_COMPILER_REVISION = 'quality-contract-compiler@1' as const;
export const QUALITY_BINDING_SCHEMA_REVISION = 'quality-bindings@1' as const;
export const QUALITY_STATE_SCHEMA_VERSION = 2 as const;

export const QUALITY_SEMANTIC_KINDS = [
  'contact',
  'motion',
  'lifecycle',
  'causality',
  'agency',
  'progression',
  'time-scale',
  'match-rules',
  'geometry',
] as const;
export type QualitySemanticKindV2 = typeof QUALITY_SEMANTIC_KINDS[number];

export const QUALITY_EVIDENCE_KINDS = [
  'deterministic-simulation',
  'native-browser',
  'geometry',
  'inventory',
  'input-replay',
  'visual-judgment',
] as const;
export type QualityEvidenceKindV2 = typeof QUALITY_EVIDENCE_KINDS[number];
export type QualityDeviceV2 = 'deterministic' | 'desktop' | 'touch';
export type QualityObligationStageV2 = 'core' | 'final';

export const QUALITY_PHASES_V2 = [
  'contract-pending',
  'spec-ready',
  'needs-design-clarification',
  'verification-unsupported',
  'core-pending',
  'core-running',
  'core-verified',
  'expanding',
  'joining-media',
  'final-verification',
  'publication-ready',
  'published',
  'failed',
  'cancelled',
] as const;
export type QualityPhaseV2 = typeof QUALITY_PHASES_V2[number];

export interface QualityArtifactRefV2 {
  path: string;
  generation: string;
  sha256: string;
  bytes: number;
  contentType: string;
  inventoryDigest?: string;
}

export interface QualityObligationV2 {
  stage: QualityObligationStageV2;
  evidenceKind: QualityEvidenceKindV2;
  device: QualityDeviceV2;
}

export interface GameQualityRequirementV2 {
  id: string;
  mandatory: boolean;
  provenance: {
    source: 'creator-request' | 'approved-design' | 'retained-contract';
    reference: string;
  };
  semantic: {
    kind: QualitySemanticKindV2;
    revision: number;
    parameters: JsonObject;
  };
  actors: string[];
  targets: string[];
  preconditions: string[];
  actions: string[];
  consequences: string[];
  forbiddenOutcomes: string[];
  obligations: QualityObligationV2[];
}

export interface GameQualityContractV2 {
  schemaVersion: 2;
  requirements: GameQualityRequirementV2[];
  resolvedConflicts: Array<{
    id: string;
    decision: string;
    authority: 'creator-request' | 'approved-design';
  }>;
}

export interface QualityRoleAuthorityV2 {
  role: string;
  allowedRoutes: Array<{ provider: string; engine: string }>;
  maxCalls: number;
  maxInputTokens: number;
  maxOutputTokens: number;
  maxExposureMicros: number;
}

export interface QualityAdmissionV2 {
  schemaVersion: 2;
  runId: string;
  commandId: string;
  creationId: string;
  version: number;
  executionKind: string;
  policyRevision: string;
  recipe: typeof VERIFIED_CORE_V2_RECIPE;
  recipeDigest: string;
  originalInput: QualityArtifactRefV2;
  lineage: {
    baseVersion: number;
    source?: QualityArtifactRefV2;
    designDigest: string;
    settingsDigest: string;
  };
  roles: QualityRoleAuthorityV2[];
  budget: {
    maxExposureMicros: number;
    repairLimit: number;
    pricingDigest: string;
  };
  authorizedMediaRoles: string[];
  acceptedAt: number;
}

export interface QualitySpecificationV2 {
  schemaVersion: 2;
  runId: string;
  finalizedDesign: QualityArtifactRefV2;
  normalizedDelta: QualityArtifactRefV2;
  contract: GameQualityContractV2;
  contractDigest: string;
  compilerRevision: string;
  registryRevision: string;
  mediaPlan: QualityArtifactRefV2;
  conflictDecisionDigest: string;
  coverageInventory: Array<{
    designItemId: string;
    requirementIds: string[];
    outcome: 'mapped' | 'unresolved';
    reasonCode?: string;
  }>;
  finalizedAt: number;
}

export interface CompiledQualityPlanV1 {
  schemaVersion: 1;
  compilerRevision: string;
  inputDigest: string;
  specificationDigest: string;
  contractDigest: string;
  registryDigest: string;
  obligations: Array<{
    obligationId: string;
    requirementId: string;
    providerId: string;
    providerRevision: string;
    stage: QualityObligationStageV2;
    evidenceKind: QualityEvidenceKindV2;
    device: QualityDeviceV2;
    schedule: JsonObject;
    resourceLimits: { maxMs: number; maxBytes: number; maxActions: number };
  }>;
  applicability: Array<{
    requirementId: string;
    obligationId: string;
    applicable: boolean;
    reasonCode: string;
  }>;
  requiredArtifacts: string[];
  requiredMediaRoles: string[];
}

export interface QualityBindingsV1 {
  schemaVersion: 1;
  sourceInventoryDigest: string;
  bindingSchemaRevision: string;
  mappings: Array<{
    requirementId: string;
    actorBindings: Record<string, string>;
    targetBindings: Record<string, string>;
    fieldBindings: Record<string, string>;
    units: Record<string, string>;
    coordinates: Record<string, string>;
  }>;
  validation: { valid: boolean; reasonCodes: string[] };
}

export interface QualityRunStateV2 {
  schemaVersion: 2;
  runId: string;
  revision: number;
  phase: QualityPhaseV2;
  basisEpoch: number;
  operationAttempt: number;
  durableDeadline: number | null;
  terminalReason?: string;
  finalManifest?: QualityArtifactRefV2;
}

export interface QualityClaimV1 {
  jobId: string;
  claimToken: string;
  leaseGeneration: number;
  leaseExpiry: number;
  expectedRunRevision: number;
}

export interface CoreEvidenceV2 {
  schemaVersion: 2;
  runId: string;
  epoch: number;
  sourceArchive: QualityArtifactRefV2;
  sourceInventoryDigest: string;
  contractDigest: string;
  planDigest: string;
  bindingDigest: string;
  geometryDigest: string;
  providerVerdictRefs: QualityArtifactRefV2[];
  mediaBasisDigest: string;
  runtimeIdentity: string;
  verifiedAt: number;
}

export interface EvidenceResultV1 {
  schemaVersion: 1;
  evidenceId: string;
  providerId: string;
  providerRevision: string;
  obligationId: string;
  requirementIds: string[];
  candidateDigest: string;
  contractDigest: string;
  bindingDigest: string;
  runtimeIdentity: string;
  device: QualityDeviceV2;
  seedIdentity: string;
  evidenceDigests: string[];
  verdict: 'covered' | 'failed' | 'unavailable';
  reasonCodes: string[];
  privateEvidence: QualityArtifactRefV2[];
  startedAt: number;
  finishedAt: number;
  resourceUse: { wallMs: number; outputBytes: number };
}

export interface ProofDebtV1 {
  code: string;
  origin: 'legacy' | 'unsupported' | 'optional-default';
  scope: string[];
}

export interface FinalEvidenceManifestV1 {
  schemaVersion: 1;
  runId: string;
  admissionDigest: string;
  specificationDigest: string;
  planDigest: string;
  coreEvidenceDigest?: string;
  /** Exact private source candidate that passed the core barrier. Core-stage
   * verdicts bind to this digest; final-stage verdicts bind to finalSource. */
  coreSource?: QualityArtifactRefV2;
  finalSource: QualityArtifactRefV2;
  finalSourceInventoryDigest: string;
  finalDist: QualityArtifactRefV2;
  finalDistInventoryDigest: string;
  media: Array<{ role: string; artifact: QualityArtifactRefV2; defaulted: boolean }>;
  settingsDigest: string;
  runtimeIdentity: string;
  shellIdentity: string;
  dependencyCatalogDigest: string;
  bindingsDigest: string;
  evidence: EvidenceResultV1[];
  safetyVerdictDigest: string;
  proofDebt: ProofDebtV1[];
  acceptanceScope: 'verified' | 'source-preserving-rebind' | 'legacy-copy';
  createdAt: number;
}

export interface QualityFailureV1 {
  schemaVersion: 1;
  stage: QualityPhaseV2;
  gateId: string;
  domain: string;
  retryClass: 'none' | 'infrastructure' | 'gameplay-repair';
  requirementIds: string[];
  candidateDigest?: string;
  reasonCode: string;
  sanitizedMessage: string;
}

export interface ProviderReservationV1 {
  schemaVersion: 1;
  reservationId: string;
  idempotencyKey: string;
  runId: string;
  role: string;
  task: string;
  attempt: number;
  allowedRoutes: Array<{ provider: string; engine: string }>;
  pricingDigest: string;
  reservedMicros: number;
  maxCalls: number;
  maxInputTokens: number;
  maxOutputTokens: number;
  state: 'reserved' | 'dispatch-intent' | 'dispatched' | 'uncertain' | 'settled' | 'cancelled';
  dispatchId?: string;
  servedProvider?: string;
  servedEngine?: string;
  usage?: { inputTokens: number; outputTokens: number; costMicros: number };
  response?: ProviderResponseV1;
  reservedAt: number;
  dispatchIntentAt?: number;
  dispatchedAt?: number;
  settledAt?: number;
  responseRecordedAt?: number;
  cancelledAt?: number;
}

export interface ProviderResponseV1 {
  text: string;
  model: string;
  usage: {
    inputTokens: number;
    outputTokens: number;
    reasoningTokens?: number;
    cacheCreationTokens: number;
    cacheCreation1hTokens?: number;
    cacheReadTokens: number;
  };
  stopReason: 'stop' | 'length' | 'stop_sequence' | 'other';
  servedBy?: { provider: string; model: string };
}

const ID = /^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,127}$/;
const LOWER_ID = /^[a-z][a-z0-9._-]{0,95}$/;

function text(value: unknown, path: string, max = 1_024): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > max) {
    contractError(path, `expected nonempty text up to ${max} characters`);
  }
  return value;
}

function stableId(value: unknown, path: string, lower = false): string {
  const valueText = text(value, path, lower ? 96 : 128);
  if (!(lower ? LOWER_ID : ID).test(valueText)) contractError(path, 'expected stable identifier');
  return valueText;
}

/** Shared validator for requirement-owned recipes and evidence. Keeping this
 * public prevents browser/replay formats from inventing subtly different IDs. */
export function validateQualityRequirementIdV2(value: unknown, path = 'requirementId'): string {
  return stableId(value, path, true);
}

function integer(value: unknown, path: string, min = 0, max = Number.MAX_SAFE_INTEGER): number {
  if (!Number.isSafeInteger(value) || Number(value) < min || Number(value) > max) {
    contractError(path, `expected integer in [${min}, ${max}]`);
  }
  return Number(value);
}

function only(value: JsonObject, path: string, keys: readonly string[]): void {
  for (const key of Object.keys(value)) if (!keys.includes(key)) contractError(`${path}.${key}`, 'unsupported field');
}

function strings(value: unknown, path: string, max: number, lowerIds = false): string[] {
  if (!Array.isArray(value) || value.length > max) contractError(path, `expected at most ${max} entries`);
  const parsed = value.map((entry, index) => lowerIds
    ? stableId(entry, `${path}[${index}]`, true)
    : text(entry, `${path}[${index}]`));
  if (new Set(parsed).size !== parsed.length) contractError(path, 'duplicate entry');
  return parsed;
}

export function validateQualityArtifactRefV2(value: unknown, path = 'artifact'): QualityArtifactRefV2 {
  const ref = validateJsonObject(value, path);
  only(ref, path, ['path', 'generation', 'sha256', 'bytes', 'contentType', 'inventoryDigest']);
  const objectPath = text(ref.path, `${path}.path`, 2_048);
  if (!objectPath.startsWith('gs://')) contractError(`${path}.path`, 'expected private gs:// object reference');
  const generation = text(ref.generation, `${path}.generation`, 128);
  if (!/^\d+$/.test(generation)) contractError(`${path}.generation`, 'expected immutable numeric generation');
  const contentType = text(ref.contentType, `${path}.contentType`, 128).toLowerCase();
  if (!/^[a-z0-9][a-z0-9.+-]*\/[a-z0-9][a-z0-9.+-]*$/.test(contentType)) {
    contractError(`${path}.contentType`, 'expected MIME type');
  }
  return {
    path: objectPath,
    generation,
    sha256: validateSha256(ref.sha256, `${path}.sha256`),
    bytes: integer(ref.bytes, `${path}.bytes`),
    contentType,
    ...(ref.inventoryDigest === undefined ? {} : {
      inventoryDigest: validateSha256(ref.inventoryDigest, `${path}.inventoryDigest`),
    }),
  };
}

function validateObligation(value: unknown, path: string): QualityObligationV2 {
  const obligation = validateJsonObject(value, path);
  only(obligation, path, ['stage', 'evidenceKind', 'device']);
  if (obligation.stage !== 'core' && obligation.stage !== 'final') contractError(`${path}.stage`, 'unsupported stage');
  if (!QUALITY_EVIDENCE_KINDS.includes(obligation.evidenceKind as QualityEvidenceKindV2)) {
    contractError(`${path}.evidenceKind`, 'unsupported evidence kind');
  }
  if (!['deterministic', 'desktop', 'touch'].includes(String(obligation.device))) {
    contractError(`${path}.device`, 'unsupported device');
  }
  return obligation as unknown as QualityObligationV2;
}

export function validateGameQualityContractV2(value: unknown): GameQualityContractV2 {
  const contract = validateJsonObject(value, 'qualityContract');
  only(contract, 'qualityContract', ['schemaVersion', 'requirements', 'resolvedConflicts']);
  if (contract.schemaVersion !== 2) contractError('qualityContract.schemaVersion', 'unsupported version; supported: 2');
  if (!Array.isArray(contract.requirements) || contract.requirements.length === 0 || contract.requirements.length > 128) {
    contractError('qualityContract.requirements', 'expected 1..128 requirements');
  }
  const seen = new Set<string>();
  const requirements = contract.requirements.map((entry, index): GameQualityRequirementV2 => {
    const path = `qualityContract.requirements[${index}]`;
    const requirement = validateJsonObject(entry, path);
    only(requirement, path, ['id', 'mandatory', 'provenance', 'semantic', 'actors', 'targets', 'preconditions', 'actions', 'consequences', 'forbiddenOutcomes', 'obligations']);
    const requirementId = stableId(requirement.id, `${path}.id`, true);
    if (seen.has(requirementId)) contractError(`${path}.id`, 'duplicate requirement id');
    seen.add(requirementId);
    if (typeof requirement.mandatory !== 'boolean') contractError(`${path}.mandatory`, 'expected boolean');
    const provenance = validateJsonObject(requirement.provenance, `${path}.provenance`);
    only(provenance, `${path}.provenance`, ['source', 'reference']);
    if (!['creator-request', 'approved-design', 'retained-contract'].includes(String(provenance.source))) {
      contractError(`${path}.provenance.source`, 'unsupported provenance');
    }
    const semantic = validateJsonObject(requirement.semantic, `${path}.semantic`);
    only(semantic, `${path}.semantic`, ['kind', 'revision', 'parameters']);
    if (!QUALITY_SEMANTIC_KINDS.includes(semantic.kind as QualitySemanticKindV2)) {
      contractError(`${path}.semantic.kind`, 'unsupported semantic kind');
    }
    if (!Array.isArray(requirement.obligations) || requirement.obligations.length === 0 || requirement.obligations.length > 12) {
      contractError(`${path}.obligations`, 'expected 1..12 explicit obligation tuples');
    }
    const obligations = requirement.obligations.map((item, itemIndex) => validateObligation(item, `${path}.obligations[${itemIndex}]`));
    const obligationKeys = obligations.map(item => `${item.stage}:${item.evidenceKind}:${item.device}`);
    if (new Set(obligationKeys).size !== obligationKeys.length) contractError(`${path}.obligations`, 'duplicate obligation tuple');
    return {
      id: requirementId,
      mandatory: requirement.mandatory,
      provenance: {
        source: provenance.source as GameQualityRequirementV2['provenance']['source'],
        reference: text(provenance.reference, `${path}.provenance.reference`, 2_048),
      },
      semantic: {
        kind: semantic.kind as QualitySemanticKindV2,
        revision: integer(semantic.revision, `${path}.semantic.revision`, 1, 1_000),
        parameters: validateJsonObject(semantic.parameters, `${path}.semantic.parameters`),
      },
      actors: strings(requirement.actors, `${path}.actors`, 32, true),
      targets: strings(requirement.targets, `${path}.targets`, 32, true),
      preconditions: strings(requirement.preconditions, `${path}.preconditions`, 32),
      actions: strings(requirement.actions, `${path}.actions`, 32),
      consequences: strings(requirement.consequences, `${path}.consequences`, 32),
      forbiddenOutcomes: strings(requirement.forbiddenOutcomes, `${path}.forbiddenOutcomes`, 32),
      obligations,
    };
  });
  if (!Array.isArray(contract.resolvedConflicts) || contract.resolvedConflicts.length > 64) {
    contractError('qualityContract.resolvedConflicts', 'expected at most 64 entries');
  }
  const resolvedConflicts = contract.resolvedConflicts.map((entry, index) => {
    const path = `qualityContract.resolvedConflicts[${index}]`;
    const conflict = validateJsonObject(entry, path);
    only(conflict, path, ['id', 'decision', 'authority']);
    if (conflict.authority !== 'creator-request' && conflict.authority !== 'approved-design') {
      contractError(`${path}.authority`, 'unsupported authority');
    }
    return {
      id: stableId(conflict.id, `${path}.id`, true),
      decision: text(conflict.decision, `${path}.decision`, 4_000),
      authority: conflict.authority,
    };
  });
  return cloneJson({ schemaVersion: 2, requirements, resolvedConflicts }) as unknown as GameQualityContractV2;
}

export function validateQualityRunStateV2(value: unknown): QualityRunStateV2 {
  const state = validateJsonObject(value, 'qualityRunState');
  only(state, 'qualityRunState', ['schemaVersion', 'runId', 'revision', 'phase', 'basisEpoch', 'operationAttempt', 'durableDeadline', 'terminalReason', 'finalManifest']);
  if (state.schemaVersion !== QUALITY_STATE_SCHEMA_VERSION) contractError('qualityRunState.schemaVersion', 'unsupported version; supported: 2');
  if (!QUALITY_PHASES_V2.includes(state.phase as QualityPhaseV2)) contractError('qualityRunState.phase', 'unsupported phase');
  if (state.durableDeadline !== null) integer(state.durableDeadline, 'qualityRunState.durableDeadline', 1);
  return {
    schemaVersion: 2,
    runId: stableId(state.runId, 'qualityRunState.runId'),
    revision: integer(state.revision, 'qualityRunState.revision'),
    phase: state.phase as QualityPhaseV2,
    basisEpoch: integer(state.basisEpoch, 'qualityRunState.basisEpoch'),
    operationAttempt: integer(state.operationAttempt, 'qualityRunState.operationAttempt'),
    durableDeadline: state.durableDeadline as number | null,
    ...(state.terminalReason === undefined ? {} : { terminalReason: text(state.terminalReason, 'qualityRunState.terminalReason', 256) }),
    ...(state.finalManifest === undefined ? {} : { finalManifest: validateQualityArtifactRefV2(state.finalManifest, 'qualityRunState.finalManifest') }),
  };
}

export function validateQualityClaimV1(value: unknown): QualityClaimV1 {
  const claim = validateJsonObject(value, 'qualityClaim');
  only(claim, 'qualityClaim', ['jobId', 'claimToken', 'leaseGeneration', 'leaseExpiry', 'expectedRunRevision']);
  return {
    jobId: stableId(claim.jobId, 'qualityClaim.jobId'),
    claimToken: stableId(claim.claimToken, 'qualityClaim.claimToken'),
    leaseGeneration: integer(claim.leaseGeneration, 'qualityClaim.leaseGeneration', 1),
    leaseExpiry: integer(claim.leaseExpiry, 'qualityClaim.leaseExpiry', 1),
    expectedRunRevision: integer(claim.expectedRunRevision, 'qualityClaim.expectedRunRevision'),
  };
}

export function validateQualityAdmissionV2(value: unknown): QualityAdmissionV2 {
  const admission = validateJsonObject(value, 'qualityAdmission');
  only(admission, 'qualityAdmission', ['schemaVersion', 'runId', 'commandId', 'creationId', 'version', 'executionKind', 'policyRevision', 'recipe', 'recipeDigest', 'originalInput', 'lineage', 'roles', 'budget', 'authorizedMediaRoles', 'acceptedAt']);
  if (admission.schemaVersion !== 2) contractError('qualityAdmission.schemaVersion', 'unsupported version; supported: 2');
  const recipe = validateJsonObject(admission.recipe, 'qualityAdmission.recipe');
  only(recipe, 'qualityAdmission.recipe', ['id', 'revision']);
  if (recipe.id !== VERIFIED_CORE_V2_RECIPE.id || recipe.revision !== VERIFIED_CORE_V2_RECIPE.revision) {
    contractError('qualityAdmission.recipe', 'unsupported recipe');
  }
  const lineage = validateJsonObject(admission.lineage, 'qualityAdmission.lineage');
  only(lineage, 'qualityAdmission.lineage', ['baseVersion', 'source', 'designDigest', 'settingsDigest']);
  const budget = validateJsonObject(admission.budget, 'qualityAdmission.budget');
  only(budget, 'qualityAdmission.budget', ['maxExposureMicros', 'repairLimit', 'pricingDigest']);
  if (!Array.isArray(admission.roles) || admission.roles.length > 16) {
    contractError('qualityAdmission.roles', 'expected 0..16 roles');
  }
  if (['code-create', 'code-iterate', 'code-fork-remix', 'code-recovery'].includes(String(admission.executionKind))
    && admission.roles.length === 0) contractError('qualityAdmission.roles', 'code acceptance requires at least one provider role');
  const roles = admission.roles.map((entry, index): QualityRoleAuthorityV2 => {
    const path = `qualityAdmission.roles[${index}]`;
    const role = validateJsonObject(entry, path);
    only(role, path, ['role', 'allowedRoutes', 'maxCalls', 'maxInputTokens', 'maxOutputTokens', 'maxExposureMicros']);
    if (!Array.isArray(role.allowedRoutes) || role.allowedRoutes.length === 0 || role.allowedRoutes.length > 8) {
      contractError(`${path}.allowedRoutes`, 'expected 1..8 routes');
    }
    return {
      role: stableId(role.role, `${path}.role`, true),
      allowedRoutes: role.allowedRoutes.map((routeEntry, routeIndex) => {
        const routePath = `${path}.allowedRoutes[${routeIndex}]`;
        const route = validateJsonObject(routeEntry, routePath);
        only(route, routePath, ['provider', 'engine']);
        return { provider: stableId(route.provider, `${routePath}.provider`, true), engine: text(route.engine, `${routePath}.engine`, 256) };
      }),
      maxCalls: integer(role.maxCalls, `${path}.maxCalls`, 0, 1_000),
      maxInputTokens: integer(role.maxInputTokens, `${path}.maxInputTokens`),
      maxOutputTokens: integer(role.maxOutputTokens, `${path}.maxOutputTokens`),
      maxExposureMicros: integer(role.maxExposureMicros, `${path}.maxExposureMicros`),
    };
  });
  if (new Set(roles.map(role => role.role)).size !== roles.length) contractError('qualityAdmission.roles', 'duplicate role');
  const parsed: QualityAdmissionV2 = {
    schemaVersion: 2,
    runId: stableId(admission.runId, 'qualityAdmission.runId'),
    commandId: stableId(admission.commandId, 'qualityAdmission.commandId'),
    creationId: stableId(admission.creationId, 'qualityAdmission.creationId'),
    version: integer(admission.version, 'qualityAdmission.version', 1),
    executionKind: stableId(admission.executionKind, 'qualityAdmission.executionKind', true),
    policyRevision: text(admission.policyRevision, 'qualityAdmission.policyRevision', 128),
    recipe: VERIFIED_CORE_V2_RECIPE,
    recipeDigest: validateSha256(admission.recipeDigest, 'qualityAdmission.recipeDigest'),
    originalInput: validateQualityArtifactRefV2(admission.originalInput, 'qualityAdmission.originalInput'),
    lineage: {
      baseVersion: integer(lineage.baseVersion, 'qualityAdmission.lineage.baseVersion'),
      ...(lineage.source === undefined ? {} : { source: validateQualityArtifactRefV2(lineage.source, 'qualityAdmission.lineage.source') }),
      designDigest: validateSha256(lineage.designDigest, 'qualityAdmission.lineage.designDigest'),
      settingsDigest: validateSha256(lineage.settingsDigest, 'qualityAdmission.lineage.settingsDigest'),
    },
    roles,
    budget: {
      maxExposureMicros: integer(budget.maxExposureMicros, 'qualityAdmission.budget.maxExposureMicros'),
      repairLimit: integer(budget.repairLimit, 'qualityAdmission.budget.repairLimit', 0, 32),
      pricingDigest: validateSha256(budget.pricingDigest, 'qualityAdmission.budget.pricingDigest'),
    },
    authorizedMediaRoles: strings(admission.authorizedMediaRoles, 'qualityAdmission.authorizedMediaRoles', 32, true),
    acceptedAt: integer(admission.acceptedAt, 'qualityAdmission.acceptedAt', 1),
  };
  const roleExposure = roles.reduce((sum, role) => sum + role.maxExposureMicros, 0);
  if (roleExposure > parsed.budget.maxExposureMicros) contractError('qualityAdmission.roles', 'role exposure exceeds run ceiling');
  return cloneJson(parsed) as unknown as QualityAdmissionV2;
}

export function validateProviderReservationV1(value: unknown): ProviderReservationV1 {
  const reservation = validateJsonObject(value, 'providerReservation');
  const allowed = ['schemaVersion', 'reservationId', 'idempotencyKey', 'runId', 'role', 'task', 'attempt', 'allowedRoutes', 'pricingDigest', 'reservedMicros', 'maxCalls', 'maxInputTokens', 'maxOutputTokens', 'state', 'dispatchId', 'servedProvider', 'servedEngine', 'usage', 'response', 'reservedAt', 'dispatchIntentAt', 'dispatchedAt', 'settledAt', 'responseRecordedAt', 'cancelledAt'];
  only(reservation, 'providerReservation', allowed);
  if (reservation.schemaVersion !== 1) contractError('providerReservation.schemaVersion', 'unsupported version; supported: 1');
  if (!['reserved', 'dispatch-intent', 'dispatched', 'uncertain', 'settled', 'cancelled'].includes(String(reservation.state))) {
    contractError('providerReservation.state', 'unsupported state');
  }
  const parsed = cloneJson(reservation) as unknown as ProviderReservationV1;
  stableId(parsed.reservationId, 'providerReservation.reservationId');
  stableId(parsed.idempotencyKey, 'providerReservation.idempotencyKey');
  stableId(parsed.runId, 'providerReservation.runId');
  stableId(parsed.role, 'providerReservation.role', true);
  text(parsed.task, 'providerReservation.task', 256);
  integer(parsed.attempt, 'providerReservation.attempt', 1);
  integer(parsed.reservedMicros, 'providerReservation.reservedMicros');
  integer(parsed.maxCalls, 'providerReservation.maxCalls', 0, 1_000);
  integer(parsed.maxInputTokens, 'providerReservation.maxInputTokens');
  integer(parsed.maxOutputTokens, 'providerReservation.maxOutputTokens');
  validateSha256(parsed.pricingDigest, 'providerReservation.pricingDigest');
  integer(parsed.reservedAt, 'providerReservation.reservedAt', 1);
  if (!Array.isArray(parsed.allowedRoutes) || parsed.allowedRoutes.length === 0 || parsed.allowedRoutes.length > 8) {
    contractError('providerReservation.allowedRoutes', 'expected 1..8 routes');
  }
  for (const [index, route] of parsed.allowedRoutes.entries()) {
    stableId(route.provider, `providerReservation.allowedRoutes[${index}].provider`, true);
    text(route.engine, `providerReservation.allowedRoutes[${index}].engine`, 256);
  }
  if (parsed.usage) {
    integer(parsed.usage.inputTokens, 'providerReservation.usage.inputTokens');
    integer(parsed.usage.outputTokens, 'providerReservation.usage.outputTokens');
    integer(parsed.usage.costMicros, 'providerReservation.usage.costMicros');
  }
  if (parsed.response) {
    const response = validateJsonObject(parsed.response, 'providerReservation.response');
    only(response, 'providerReservation.response', ['text', 'model', 'usage', 'stopReason', 'servedBy']);
    text(response.text, 'providerReservation.response.text', 20_000_000);
    text(response.model, 'providerReservation.response.model', 256);
    if (!['stop', 'length', 'stop_sequence', 'other'].includes(String(response.stopReason))) {
      contractError('providerReservation.response.stopReason', 'unsupported stop reason');
    }
    const responseUsage = validateJsonObject(response.usage, 'providerReservation.response.usage');
    only(responseUsage, 'providerReservation.response.usage', ['inputTokens', 'outputTokens', 'reasoningTokens', 'cacheCreationTokens', 'cacheCreation1hTokens', 'cacheReadTokens']);
    for (const key of ['inputTokens', 'outputTokens', 'cacheCreationTokens', 'cacheReadTokens'] as const) {
      integer(responseUsage[key], `providerReservation.response.usage.${key}`);
    }
    for (const key of ['reasoningTokens', 'cacheCreation1hTokens'] as const) {
      if (responseUsage[key] !== undefined) integer(responseUsage[key], `providerReservation.response.usage.${key}`);
    }
    if (response.servedBy !== undefined) {
      const servedBy = validateJsonObject(response.servedBy, 'providerReservation.response.servedBy');
      only(servedBy, 'providerReservation.response.servedBy', ['provider', 'model']);
      stableId(servedBy.provider, 'providerReservation.response.servedBy.provider', true);
      text(servedBy.model, 'providerReservation.response.servedBy.model', 256);
    }
    if (parsed.state !== 'settled' || !parsed.usage || parsed.responseRecordedAt === undefined
      || parsed.usage.inputTokens !== responseUsage.inputTokens
      || parsed.usage.outputTokens !== responseUsage.outputTokens) {
      contractError('providerReservation.response', 'durable response requires matching settled usage and timestamp');
    }
  }
  if (parsed.responseRecordedAt != null) integer(parsed.responseRecordedAt, 'providerReservation.responseRecordedAt', 1);
  return parsed;
}

export function validateQualitySchema(value: unknown): GameQualityContractV2 | QualityAdmissionV2 | QualityRunStateV2 | ProviderReservationV1 {
  const object = validateJsonObject(value, 'qualitySchema');
  if (object.schemaVersion === 2 && Object.hasOwn(object, 'requirements')) return validateGameQualityContractV2(object);
  if (object.schemaVersion === 2 && Object.hasOwn(object, 'commandId')) return validateQualityAdmissionV2(object);
  if (object.schemaVersion === 2 && Object.hasOwn(object, 'phase')) return validateQualityRunStateV2(object);
  if (object.schemaVersion === 1 && Object.hasOwn(object, 'reservationId')) return validateProviderReservationV1(object);
  contractError('qualitySchema', 'unknown schema version or shape');
}
